
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // SEO Meta Tags für Login
    document.title = 'Mitglieder-Login - IPSAS EPSAS Deutschland';
    
    // Meta Description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Mitglieder-Login für IPSAS EPSAS Deutschland Plattform. Zugang zu exklusiven Ressourcen, Standards und Tools für Public Sector Accounting.');
    }
    
    // Meta Keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Login, Mitglieder, IPSAS, EPSAS, Anmeldung, Zugang');
    }
    
    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/login`);
    }
    
    // Robots - Login-Seiten sollten nicht indexiert werden
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) {
      robots.setAttribute('content', 'noindex, nofollow');
    }
    
    // Last Modified
    const lastModified = document.querySelector('meta[http-equiv="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Mock login - always fails
    setTimeout(() => {
      setError('Benutzername oder Passwort ungültig');
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold text-blue-900 cursor-pointer" style={{ fontFamily: '"Pacifico", serif' }}>
                  IPSAS-EPSAS
                </h1>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="px-6 py-2.5 text-blue-900 font-semibold hover:text-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Zurück zur Startseite
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Login Form */}
      <div className="flex items-center justify-center py-20">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Mitglieder-Login
              </h2>
              <p className="text-gray-600">
                Melden Sie sich an, um auf alle Ressourcen zuzugreifen
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                  placeholder="ihre.email@beispiel.de"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Passwort
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                  placeholder="Ihr Passwort"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 bg-blue-900 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Anmeldung läuft...
                  </div>
                ) : (
                  'Anmelden'
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-4">
                  Noch kein Mitglied?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="ri-mail-line text-blue-600 text-lg mr-3 mt-0.5"></i>
                    <div className="text-left">
                      <p className="text-blue-900 font-medium text-sm mb-1">
                        Registrierung per E-Mail
                      </p>
                      <p className="text-blue-700 text-sm mb-2">
                        Senden Sie eine E-Mail an:
                      </p>
                      <a 
                        href="mailto:registration@ipsas-epsas.de?subject=Registrierungsanfrage&body=Hallo,%0D%0A%0D%0AIch möchte mich gerne für die IPSAS-EPSAS Plattform registrieren.%0D%0A%0D%0AMit freundlichen Grüßen"
                        className="text-blue-900 font-semibold hover:text-blue-700 transition-colors cursor-pointer"
                      >
                        registration@ipsas-epsas.de
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <a 
                href="mailto:support@ipsas-epsas.de?subject=Passwort vergessen"
                className="text-blue-900 text-sm hover:text-blue-700 transition-colors cursor-pointer"
              >
                Passwort vergessen?
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h5 className="text-white font-bold text-lg mb-4">IPSAS-EPSAS</h5>
              <p className="text-sm leading-relaxed">
                Ihre zentrale Plattform für internationale und europäische 
                Standards der öffentlichen Rechnungslegung
              </p>
            </div>

            <div>
              <h5 className="text-white font-bold text-lg mb-4">Plattform</h5>
              <ul className="space-y-2 text-sm">
                <li>
                  <a 
                    href="mailto:registration@ipsas-epsas.de?subject=Registrierungsanfrage"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Registrierung
                  </a>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors cursor-pointer">
                    Login
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-bold text-lg mb-4">Ressourcen</h5>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/login" className="hover:text-white transition-colors cursor-pointer">
                    IPSAS Standards
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors cursor-pointer">
                    EPSAS Standards
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors cursor-pointer">
                    Public Sector Accounting
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-bold text-lg mb-4">Kontakt</h5>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/impressum" className="hover:text-white transition-colors cursor-pointer">
                    Impressum
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors cursor-pointer">
                    Datenschutz
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm">
            <p>&copy; 2024 IPSAS-EPSAS.de. Alle Rechte vorbehalten.</p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              className="text-gray-400 hover:text-white transition-colors cursor-pointer mt-4 md:mt-0"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
